import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  apiRequest,
  SuccessToast,
  ErrorToast,
  toastConfig,
  validatePhoneNumber,
  devError,
} from "@ridecloud/shared";
import SignupOtp from "../components/signupPage/SignupOtp";
import SignupSuccess from "../components/signupPage/SignupSuccess";
import { Toaster } from "react-hot-toast";
import { ArrowLeft, UserPlus, User, Phone, Mail, Lock } from "lucide-react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { LoaderInside } from "../components/Loader";

const Signup = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("signup");
  const [loading, setLoading] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
    confirm_password: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhoneChange = (e) => {
    const formattedPhone = validatePhoneNumber(e.target.value);
    setFormData({ ...formData, phone: formattedPhone });
  };

  const validateForm = () => {
    if (!formData.email) {
      ErrorToast("Email is required");
      return false;
    }
    if (!formData.email.endsWith("@gmail.com")) {
      ErrorToast("Please enter a valid Gmail address.");
      return false;
    }
    if (formData.password.length < 8) {
      ErrorToast("Password must be at least 8 characters long");
      return false;
    }
    if (!/[A-Z]/.test(formData.password)) {
      ErrorToast("Password must contain at least one uppercase letter");
      return false;
    }
    if (!/[a-z]/.test(formData.password)) {
      ErrorToast("Password must contain at least one lowercase letter");
      return false;
    }
    if (!/[0-9]/.test(formData.password)) {
      ErrorToast("Password must contain at least one number");
      return false;
    }
    if (!/[!@#$%^&*]/.test(formData.password)) {
      ErrorToast("Password must contain at least one special character");
      return false;
    }
    if (formData.password.includes(" ")) {
      ErrorToast("Password must not contain spaces");
      return false;
    }

    if (formData.password !== formData.confirm_password) {
      ErrorToast("Passwords do not match.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    try {
      await apiRequest(async (APIv1) =>
        APIv1.post("/auth/temp-signup", formData, {
          headers: {
            "Content-Type": "application/json",
          },
        })
      );

      SuccessToast(
        "Account created successfully. A verification email has been sent to your inbox."
      );
      setFormData({
        ...formData,
        password: "",
        confirm_password: "",
      });

      setStep("otp");
    } catch (err) {
      devError("Error during signup:", err);
      ErrorToast(
        err.response?.data?.message ||
          "An error occurred. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSuccess = () => {
    setStep("success");
  };

  if (step === "success") {
    return <SignupSuccess email={formData.email} name={formData.first_name} />;
  }

  if (step === "otp") {
    return (
      <SignupOtp
        email={formData.email}
        onSuccess={handleOTPSuccess}
        onBack={() => setStep("signup")}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-green-50 to-green-100 flex items-center justify-center relative overflow-hidden px-4">
      {/* Blurry gradient blobs like homepage */}
      <div className="absolute w-96 h-96 -top-32 -left-32 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full opacity-20 blur-3xl animate-pulse" />
      <div className="absolute w-80 h-80 bottom-0 -right-20 bg-gradient-to-br from-teal-400 to-green-500 rounded-full opacity-20 blur-3xl animate-pulse delay-1000" />

      {/* Card */}
      <div className="relative z-10 w-full max-w-md bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-emerald-100 animate-fade-in-up">
        {/* Back Button */}
        <button
          onClick={() => navigate("/")}
          className="absolute top-4 left-4 p-2 rounded-xl hover:bg-emerald-50 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-emerald-600" />
        </button>

        {/* Logo */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
            <img
              src="/img/ride_cloud.png"
              alt="RideCloud"
              className="w-12 h-12"
            />
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-emerald-500 to-teal-600 bg-clip-text text-transparent">
            Join RideCloud
          </h2>
          <p className="text-gray-600 mt-2">
            Help us identify yourself by filling the details below
          </p>
        </div>

        <Toaster {...toastConfig} />

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name Group */}
          <div className="grid grid-cols-2 gap-4">
            <div className="relative">
              <User className="absolute left-4 top-3.5 w-5 h-5 text-emerald-500" />
              <input
                type="text"
                id="first_name"
                name="first_name"
                placeholder="First Name"
                value={formData.first_name}
                onChange={handleChange}
                required
                className="w-full pl-12 pr-4 py-3 rounded-2xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
              />
            </div>
            <div className="relative">
              <User className="absolute left-4 top-3.5 w-5 h-5 text-emerald-500" />
              <input
                type="text"
                id="last_name"
                name="last_name"
                placeholder="Last Name"
                value={formData.last_name}
                onChange={handleChange}
                required
                className="w-full pl-12 pr-4 py-3 rounded-2xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
              />
            </div>
          </div>

          {/* Email */}
          <div className="relative">
            <Mail className="absolute left-4 top-3.5 w-5 h-5 text-emerald-500" />
            <input
              type="email"
              id="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full pl-12 pr-4 py-3 rounded-2xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
            />
          </div>

          {/* Phone */}

          <div className="relative">
            <div className="flex">
              <div className="flex items-center px-3 border border-r-0 border-gray-200 rounded-l-2xl bg-gray-50 min-w-[60px]">
                <span className="text-gray-600 text-sm font-medium whitespace-nowrap">
                  +91
                </span>
              </div>
              <input
                type="tel"
                id="phone"
                name="phone"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={handlePhoneChange}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                pattern="[0-9]*"
                inputMode="numeric"
                required
                className="flex-1 min-w-0 pl-4 pr-4 py-3 rounded-r-2xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
              />
            </div>
          </div>

          <div className="relative">
            <Lock className="absolute left-4 top-3.5 w-5 h-5 text-emerald-500" />
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              onFocus={() => setIsFocused(true)}
              required
              className="w-full pl-12 pr-12 py-3 rounded-2xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
            />
            {isFocused && formData.password.length > 0 && (
              <span
                className="absolute right-4 top-3.5 cursor-pointer text-gray-500 hover:text-emerald-600 transition-colors"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <VisibilityOff fontSize="small" />
                ) : (
                  <Visibility fontSize="small" />
                )}
              </span>
            )}
          </div>
          <div className="relative">
            <Lock className="absolute left-4 top-3.5 w-5 h-5 text-emerald-500" />
            <input
              type={showConfirmPassword ? "text" : "password"}
              id="confirm_password"
              name="confirm_password"
              placeholder="Confirm Password"
              value={formData.confirm_password}
              onChange={handleChange}
              onFocus={() => setIsFocused(true)}
              required
              className="w-full pl-12 pr-12 py-3 rounded-2xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
            />
            {isFocused && formData.confirm_password.length > 0 && (
              <span
                className="absolute right-4 top-3.5 cursor-pointer text-gray-500 hover:text-emerald-600 transition-colors"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <VisibilityOff fontSize="small" />
                ) : (
                  <Visibility fontSize="small" />
                )}
              </span>
            )}
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center items-center gap-2 py-3 rounded-2xl font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-600 shadow-lg hover:shadow-emerald-500/30 hover:scale-105 transition-all duration-300"
          >
            {loading ? <LoaderInside /> : <UserPlus className="w-5 h-5" />}
            Create Account
          </button>
        </form>

        {/* Separator */}
        <div className="flex items-center gap-4 my-6">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-gray-400 text-sm font-medium">OR</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {/* Login link */}
        <p className="text-center mt-6 text-gray-600 text-sm">
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-semibold text-emerald-600 hover:underline"
          >
            Log In
          </Link>
        </p>
      </div>

      {/* Animations */}
      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default Signup;
